import { marginalAnnualValue, type BillingFrequency, type PricingTier } from "./pricing";

/* ------------------------------------------------------------------ */
/* Segmentation                                                        */
/* ------------------------------------------------------------------ */

export interface SegmentTier {
  id: string;
  name: string;
  minSites: number;
  /** null means "and above". */
  maxSites: number | null;
  color: string;
  sortOrder: number;
}

/** The segment is derived from site count only. It is never stored. */
export function segmentFor(tiers: SegmentTier[], siteCount: number): SegmentTier | null {
  const sorted = tiers.slice().sort((a, b) => a.minSites - b.minSites);
  for (const t of sorted) {
    if (siteCount >= t.minSites && siteCount <= (t.maxSites ?? Infinity)) return t;
  }
  return null;
}

export function bandLabel(t: { minSites: number; maxSites: number | null }): string {
  if (t.maxSites === null) return `${t.minSites}+`;
  if (t.maxSites === t.minSites) return `${t.minSites}`;
  return `${t.minSites}–${t.maxSites}`;
}

/* ------------------------------------------------------------------ */
/* Unrealised potential                                                */
/* ------------------------------------------------------------------ */

export interface Potential {
  /** Sites at the provider not covered by the deal(s). */
  sites: number;
  /** Annual value (exc VAT) of the uncovered sites at list price. */
  annualValue: number;
}

/**
 * Upside is deal-based only: it is the provider's total site count against
 * the sites the deal (or the account's won deals) cover. It never reflects a
 * provider's stated wish to grow.
 */
export function unrealisedPotential(
  tiers: PricingTier[],
  frequency: BillingFrequency,
  totalSites: number,
  coveredSites: number,
): Potential {
  const sites = Math.max(0, totalSites - coveredSites);
  return { sites, annualValue: marginalAnnualValue(tiers, coveredSites, totalSites, frequency) };
}

/* ------------------------------------------------------------------ */
/* Stage rules                                                         */
/* ------------------------------------------------------------------ */

export type StageOutcome = "open" | "won" | "lost";

/** How a suppression came about: what the person asked through, or the system event behind it. */
export const SUPPRESSION_CHANNELS = ["email", "phone", "in_person", "web_form", "reply", "bounce", "complaint", "other"] as const;
export type SuppressionChannel = (typeof SUPPRESSION_CHANNELS)[number];
export const SUPPRESSION_CHANNEL_LABEL: Record<SuppressionChannel, string> = {
  email: "Email",
  phone: "Phone",
  in_person: "In person",
  web_form: "Web form",
  reply: "Reply to outreach",
  bounce: "Email bounced",
  complaint: "Spam complaint",
  other: "Other",
};
/** The ones a person picks when recording a suppression or opt-out; the rest are set by the system. */
export const PICKABLE_SUPPRESSION_CHANNELS: SuppressionChannel[] = ["email", "phone", "in_person", "web_form", "other"];

export const REPLY_OUTCOMES = ["interested", "not_now", "no_thanks", "wrong_person", "bounced"] as const;
export type ReplyOutcome = (typeof REPLY_OUTCOMES)[number];
export const REPLY_OUTCOME_LABEL: Record<ReplyOutcome, string> = {
  interested: "Interested",
  not_now: "Not now",
  no_thanks: "No thanks",
  wrong_person: "Wrong person",
  bounced: "Bounced",
};

export interface StageRuleInput {
  id: string;
  name: string;
  sortOrder: number;
  outcome: StageOutcome;
  requiresActivity: boolean;
  requiredActivityTypeId: string | null;
}

export interface ActivityLike {
  typeId: string;
  occurredAt: string | Date;
}

/**
 * A stage flagged "required activity before advancing" blocks forward moves
 * until the activity is logged. With a specific activity type (e.g. Demo),
 * one such activity anywhere on the deal satisfies it; with no type, any
 * activity since the deal entered the stage does.
 *
 * Backward moves and moves to a lost stage are never blocked.
 * Returns a human-readable reason when blocked, otherwise null.
 */
export function advanceBlockedReason(
  from: StageRuleInput,
  to: StageRuleInput,
  stageEnteredAt: string | Date,
  activities: ActivityLike[],
  activityTypeName: (id: string) => string,
): string | null {
  if (!from.requiresActivity) return null;
  if (to.outcome === "lost") return null;
  if (to.sortOrder <= from.sortOrder) return null;
  if (from.requiredActivityTypeId) {
    const ok = activities.some((a) => a.typeId === from.requiredActivityTypeId);
    return ok ? null : `Log a ${activityTypeName(from.requiredActivityTypeId)} before moving out of ${from.name}.`;
  }
  const since = new Date(stageEnteredAt).getTime();
  const ok = activities.some((a) => new Date(a.occurredAt).getTime() >= since);
  return ok ? null : `Log an activity in ${from.name} before moving the deal on.`;
}

/* ------------------------------------------------------------------ */
/* Account risk                                                        */
/* ------------------------------------------------------------------ */

export type RiskLevel = "healthy" | "watch" | "at_risk";
export type BillingStatus = "unbilled" | "awaiting_payment" | "overdue" | "paid";

export const BILLING_STATUS_LABEL: Record<BillingStatus, string> = {
  unbilled: "Unbilled",
  awaiting_payment: "Awaiting payment",
  overdue: "Overdue",
  paid: "Paid",
};

export type RiskSource = "usage" | "billing" | "flag" | "lifecycle" | "relationship";

export interface RiskSignal {
  source: RiskSource;
  level: Exclude<RiskLevel, "healthy">;
  label: string;
  detail?: string;
}

export function riskLevel(signals: RiskSignal[]): RiskLevel {
  if (signals.some((s) => s.level === "at_risk")) return "at_risk";
  if (signals.some((s) => s.level === "watch")) return "watch";
  return "healthy";
}

/** Percentage drop between the earlier and later window of a usage series. */
export function usageDropPct(series: number[], windowSize: number): number | null {
  if (series.length < windowSize * 2) return null;
  const recent = series.slice(-windowSize);
  const prior = series.slice(-windowSize * 2, -windowSize);
  const avg = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / xs.length;
  const p = avg(prior);
  if (p <= 0) return null;
  return Math.round(((p - avg(recent)) / p) * 1000) / 10;
}

/**
 * Whether an email needs a topic. Filter-based sends and "opted in to a topic"
 * selections go only to people who chose the topic; a hand-picked "everyone"
 * or "key contacts" email doesn't depend on topic choices.
 */
export function topicRequired(audience: { selection?: { who: "all" | "key" | "topic" } | null } | null | undefined): boolean {
  return !audience?.selection || audience.selection.who === "topic";
}

/* ------------------------------------------------------------------ */
/* Free periods and discounts                                          */
/* ------------------------------------------------------------------ */

/**
 * A won deal can start with a free period, as Otii's £0 Xero items: a pilot
 * or a trial (free until someone marks it converted), or free onboarding
 * (paying starts by itself the day after its last free day).
 */
export type FreeKind = "pilot" | "trial" | "onboarding";

export const FREE_KINDS: { id: FreeKind; label: string; converts: boolean }[] = [
  { id: "pilot", label: "Free pilot", converts: true },
  { id: "trial", label: "Free trial", converts: true },
  { id: "onboarding", label: "Free onboarding", converts: false },
];

export const freeKindLabel = (k: FreeKind) => FREE_KINDS.find((f) => f.id === k)?.label ?? "Free period";

/**
 * planned: the deal isn't won yet. running: won, still free. ended: past its
 * last day and not converted, so it needs a decision (pilots and trials
 * only). converted: paying from `convertedOn`.
 */
export type PilotState = "planned" | "running" | "ended" | "converted";

export interface DealPilot {
  kind: FreeKind;
  /** The last free day. */
  endsOn: string;
  /** Paying from this day, once converted. */
  convertedOn: string | null;
  state: PilotState;
  /** Running: days until the last free day (0 on the day itself). */
  daysLeft: number | null;
}

export const nextDay = (day: string) => new Date(Date.parse(`${day}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10);

export function pilotOf(d: { status: StageOutcome; pilotKind?: FreeKind | null; pilotEndsOn: string | null; pilotConvertedOn: string | null }, today: string): DealPilot | null {
  if (!d.pilotEndsOn) return null;
  const kind = d.pilotKind ?? "pilot";
  const endsOn = d.pilotEndsOn;
  if (d.pilotConvertedOn) return { kind, endsOn, convertedOn: d.pilotConvertedOn, state: "converted", daysLeft: null };
  if (d.status !== "won") return { kind, endsOn, convertedOn: null, state: "planned", daysLeft: null };
  const days = Math.round((Date.parse(`${endsOn}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000);
  if (days >= 0) return { kind, endsOn, convertedOn: null, state: "running", daysLeft: days };
  // Free onboarding needs no decision: paying starts the day after its last free day.
  if (kind === "onboarding") return { kind, endsOn, convertedOn: nextDay(endsOn), state: "converted", daysLeft: null };
  return { kind, endsOn, convertedOn: null, state: "ended", daysLeft: null };
}

/** Won and still free (running, or ended without converting): its value isn't revenue yet. */
export const isFreePilot = (p: DealPilot | null | undefined): boolean => p?.state === "running" || p?.state === "ended";

/** The day a won deal starts paying: the day it was won, or when its free period converted. Null until then. */
export function payingFromOf(d: { status: StageOutcome; wonAt: string | null }, pilot: DealPilot | null): string | null {
  if (d.status !== "won") return null;
  if (pilot) return pilot.convertedOn;
  return d.wonAt?.slice(0, 10) ?? null;
}

/**
 * A discount on the licence for a period, as Xero's discount % on the
 * invoice lines. It runs from `from` (or, when that isn't set, from when
 * paying starts) to `until`, its last day. Add-ons aren't discounted.
 */
export interface DealDiscount {
  pct: number;
  /** The first discounted day, if set; null: from when paying starts. */
  from: string | null;
  /** The last discounted day. */
  until: string;
  /** The first discounted day: `from`, or when paying starts (null until that's known). */
  startsOn: string | null;
  state: "planned" | "running" | "ended";
  /** What the deal bills a year while the discount runs. */
  discountedValue: number;
}

export function discountOf(
  d: { discountPct: number | null; discountFrom: string | null; discountUntil: string | null },
  values: { licenceValue: number; addOnValue: number },
  payingFrom: string | null,
  today: string,
): DealDiscount | null {
  if (!d.discountPct || !d.discountUntil) return null;
  const startsOn = d.discountFrom ?? payingFrom;
  return {
    pct: d.discountPct,
    from: d.discountFrom,
    until: d.discountUntil,
    startsOn,
    state: today > d.discountUntil ? "ended" : startsOn && today >= startsOn ? "running" : "planned",
    discountedValue: Math.round((values.licenceValue * (1 - d.discountPct / 100) + values.addOnValue) * 100) / 100,
  };
}

/**
 * What a deal bills a year on `day`, if it starts paying on `payingFrom`:
 * nothing before then, the licence less any discount running that day, and
 * its add-ons.
 */
export function billedOn(
  d: { licenceValue: number; addOnValue: number; discount: Pick<DealDiscount, "pct" | "from" | "until"> | null },
  payingFrom: string | null,
  day: string,
): number {
  if (!payingFrom || day < payingFrom) return 0;
  const disc = d.discount && (d.discount.from ?? payingFrom) <= day && day <= d.discount.until ? d.discount.pct : 0;
  return Math.round((d.licenceValue * (1 - disc / 100) + d.addOnValue) * 100) / 100;
}
